import { Conversation } from '~api';
import { api } from '~portal/api';
import { logger } from '~portal/lib';

/**
 * @desc Shared rename-conversation flow: the `conversations.rename` mutation with an optimistic
 * update of the sidebar listing (the row is being edited in place, so waiting for a round-trip
 * would make the title flicker back), rolled back and re-fetched if the write fails.
 *
 * Renaming also locks the conversation's title server-side — the agent stops generating
 * summaries for it.
 */
export function useRenameConversation() {
  const utils = api.useUtils();

  const renameMutation = api.conversations.rename.useMutation({
    onMutate: async ({ conversationId, title }) => {
      // Stop an in-flight list refetch from overwriting the optimistic title.
      await utils.conversations.list.cancel();
      const previous = utils.conversations.list.getData();

      utils.conversations.list.setData(undefined, (old) =>
        old?.map((conv) => (conv._id === conversationId ? { ...conv, summary: title } : conv)),
      );

      return { previous };
    },
    onError: (err, variables, context) => {
      if (context?.previous) utils.conversations.list.setData(undefined, context.previous);
      logger.error('[useRenameConversation] Rename conversation failed', {
        conversationId: variables.conversationId,
        error: err.message,
      });
    },
    onSettled: () => {
      utils.conversations.list.invalidate().catch((err) => {
        logger.error('[useRenameConversation] Failed to invalidate conversations list', {
          error: err instanceof Error ? err.message : String(err),
        });
      });
    },
  });

  /**
   * @desc Commit a new title. Trims and rejects an empty or unchanged title without a round-trip;
   * returns whether a write was issued.
   */
  const rename = (conversationId: string, title: string, currentTitle?: string | null) => {
    if (!conversationId || conversationId === 'new') return false;

    const trimmed = title.trim().slice(0, Conversation.TitleMaxLength);
    if (!trimmed || trimmed === currentTitle) return false;

    renameMutation.mutate({ conversationId, title: trimmed });
    return true;
  };

  return { rename, isRenaming: renameMutation.isPending };
}
